import { cn } from "@/utils/styles";
import { WalletType, getWalletOption } from "@/utils/wallet";
import { useField } from "formik";
import { useMemo } from "react";
import { SignupErrorFeedback } from "../SignupErrorFeedback/SignupErrorFeedback";
import { cva, type VariantProps } from "class-variance-authority";

type WalletAddressFieldProps = VariantProps<typeof walletAddressFieldVariants> & {
  name: string;
  walletType: WalletType;
  className?: string;
};

export const WalletAddressField: React.FC<WalletAddressFieldProps> = ({
  name,
  walletType,
  variant,
  size,
  className
}) => {
  const [field, meta] = useField(name);
  const showError = meta.touched && meta.error;
  const wallet = useMemo(() => getWalletOption(walletType), [walletType]);
  if (!wallet) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2" data-testid="WalletAddressField">
      <div className={cn(walletAddressFieldVariants({ variant, size, className }))}>
        <wallet.icon className="absolute left-[10px] h-[18px]" />
        <input
          type="text"
          className="subtext-small h-full flex-1 rounded border-none bg-black px-3 pl-[40px] focus-visible:ring-2 focus-visible:ring-white"
          placeholder="Enter address"
          autoComplete="off"
          {...field}
        />
      </div>
      {showError && <SignupErrorFeedback>{meta.error}</SignupErrorFeedback>}
    </div>
  );
};

const walletAddressFieldVariants = cva(
  "relative flex select-none flex-row items-center justify-center rounded border bg-gray-dark-600 label-medium transition-all",
  {
    variants: {
      variant: {
        error: "border-red-light-700",
        default: "border-gray-dark-900"
      },
      size: {
        default: "h-[48px]",
        sm: "h-[36px]"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
